import React, { useState, useMemo } from 'react';
import pluralize from 'pluralize';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import DownloadIcon from '@mui/icons-material/DownloadRounded';

import { useNotifications } from 'src/notifications';
import { StringFormat, formatString } from 'src/utils/format-string';
import { useCreateProperties, usePropertyLabels, useCreatePropertyLabel } from 'src/services/api';
import type { IStandardErrorResponse } from 'src/api/common';
import { Flex, FlexColumn } from 'src/components/flex';
import LabelChip, { PROPERTY_LABEL_CHIP_COLOR } from 'src/components/labels/LabelChip';
import ButtonLink from 'src/components/button-link/ButtonLink';
import FileUpload from 'src/components/input/file-upload/FileUpload';
import SimpleTable from 'src/components/table/simple-table/SimpleTable';
import { type ParsedCsv, parseCsvFromFile } from 'src/utils/csv';
import {
    type PropertyCsvRow,
    parseProperties,
    importPropertiesFields,
    getUnknownPropertyFields
} from 'src/components/bulk-import/parse-properties';
import ImportPropertiesLabel from 'src/components/bulk-import/ImportPropertiesLabel';

import propertyImportTemplateUrl from 'src/assets/csv-templates/property-import-template.csv?url';

type Props = {
    info?: string;
    isOpened: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export default function ImportProperties({
    info,
    isOpened,
    onClose,
    onSuccess
}: Props) {
    const { addNotification } = useNotifications();

    const { mutateAsync: createProperties, isLoading: isCreatingProperties } = useCreateProperties();
    const { data: existingLabels, refetch: refetchLabels } = usePropertyLabels();
    const { mutateAsync: createLabel, isLoading: isCreatingLabels } = useCreatePropertyLabel();

    const [parsedFile, setParsedFile] = useState<ParsedCsv<PropertyCsvRow>>();
    const [parsedFileName, setParsedFileName] = useState<string>();
    const [parsedFileCreatedAt, setParsedFileCreatedAt] = useState<string>();
    const [newProperties, setNewProperties] = useState<ReturnType<typeof parseProperties>>([]);
    const [failedPropertyNames, setFailedPropertyNames] = useState<string[]>([]);
    const [propertyErrors, setPropertyErrors] = useState<string[]>([]);
    const [showAllErrors, setShowAllErrors] = useState(false);
    const [numImportedProperties, setNumImportedProperties] = useState<number>(0);
    const [unknownFields, setUnknownFields] = useState<string[]>([]);

    const [labelsInCsv, setLabelsInCsv] = useState<string[][]>([]);
    const [removedLabels, setRemovedLabels] = useState<string[]>([]);
    const [hasLabelsColumn, setHasLabelsColumn] = useState(false);
    const [labelsConfirmed, setLabelsConfirmed] = useState(false);

    const uniqueLabelsInCsv = useMemo(
        () => Array.from(new Set(labelsInCsv.flat())).sort((a, b) => a.localeCompare(b)),
        [labelsInCsv]
    );

    const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setFailedPropertyNames([]);
        setPropertyErrors([]);
        setNumImportedProperties(0);
        setLabelsConfirmed(false);

        try {
            if (e.target.files) {
                const newFile = e.target.files[0];
                const newParsedFile = await parseCsvFromFile<PropertyCsvRow>(newFile);

                setParsedFile(newParsedFile);
                setParsedFileName(newFile.name);
                setParsedFileCreatedAt(new Date().toISOString());
                setNewProperties(parseProperties(newParsedFile));
                setUnknownFields(getUnknownPropertyFields(newParsedFile));

                const hasLabels = newParsedFile.header.includes('labels');
                setHasLabelsColumn(hasLabels);

                if (hasLabels) {
                    const labelsPerRow: string[][] = newParsedFile.body.map(row => {
                        const labelsString = row.labels;
                        if (labelsString && labelsString.trim()) {
                            return labelsString
                                .split(':')
                                .map(label => label.trim())
                                .filter(label => label.length > 0);
                        }
                        return [];
                    });
                    setLabelsInCsv(labelsPerRow);

                    const hasAnyLabels = labelsPerRow.some(labels => labels.length > 0);
                    if (!hasAnyLabels) {
                        setHasLabelsColumn(false);
                    }
                } else {
                    setLabelsInCsv([]);
                }
            }
        } catch (error) {
            if (error instanceof Error) {
                addNotification(error.message, 'error');
            } else {
                addNotification('An error occurred while parsing the file', 'error');
            }
        }
    };

    const handleCreateLabels = async () => {
        if (!existingLabels) {
            return;
        }

        try {
            const existingLabelNames = new Set(existingLabels.map(l => l.name));
            const labelsToCreate = uniqueLabelsInCsv.filter(label => !existingLabelNames.has(label));

            for (const labelName of labelsToCreate) {
                await createLabel({ name: labelName });
            }

            await refetchLabels();

            setLabelsConfirmed(true);

            if (labelsToCreate.length > 0) {
                addNotification(
                    `${labelsToCreate.length} new ${pluralize('label', labelsToCreate.length)} created successfully`,
                    'success'
                );
            }
        } catch (error) {
            console.error(error);
            const errorMessage = (error as IStandardErrorResponse).msg ? (error as IStandardErrorResponse).msg : 'Failed to create labels';
            addNotification(errorMessage, 'error');
        }
    };

    const handleSubmit = async () => {
        try {
            const properties = newProperties
                .filter(o => !o.errors?.length)
                .map((o, index) => {
                    const labelNamesForRow = labelsInCsv[index] || [];

                    const labelObjects = labelNamesForRow
                        .map(name => existingLabels?.find(l => l.name === name))
                        .filter((label)=> label !== undefined);


                    return {
                        ...o.property,
                        labels: labelObjects.length > 0 ? labelObjects : undefined,
                    };
                });

            const {
                num_added: numAddedProperties,
                num_failed: numFailedProperties,
                failed_property_names: _failedPropertyNames,
                errors,
            } = await createProperties({ properties });

            if (numAddedProperties > 0) {
                const nSuccess = numAddedProperties;
                onSuccess?.();
                setNumImportedProperties(nSuccess);

                if (numFailedProperties === 0) {
                    addNotification(`${nSuccess} ${pluralize('property', nSuccess)} imported successfully`, 'success');

                    setParsedFile(undefined);
                    setParsedFileName(undefined);
                    setNewProperties([]);
                    setUnknownFields([]);
                    setLabelsInCsv([]);
                    setHasLabelsColumn(false);
                    setLabelsConfirmed(false);
                } else {
                    addNotification(
                        `Only ${nSuccess}/${newProperties.length} properties imported successfully`,
                        'warning'
                    );
                    setParsedFile(undefined);
                    setParsedFileName(undefined);
                    setParsedFileCreatedAt(undefined);
                    setNewProperties([]);
                    setFailedPropertyNames(_failedPropertyNames);
                    setUnknownFields([]);
                    setLabelsInCsv([]);
                    setHasLabelsColumn(false);
                    setLabelsConfirmed(false);
                }
            } else {
                addNotification('Failed to import any properties', 'error');
            }
            if (errors && Object.keys(errors).length > 0) {
                const apiErrorLines = Object.entries(errors as Record<string, { msg?: string }>)
                    .map(([submittedIndex, error]) => {
                        const message = error.msg || 'Unknown error';
                        const label = `Property ${Number(submittedIndex) + 1}`;
                        return `${label}: ${message}`;
                    });
                if (apiErrorLines.length > 0) {
                    setPropertyErrors(apiErrorLines);
                    setShowAllErrors(false);
                    console.error('Import properties errors:', apiErrorLines.join('\n'));
                }
            }
        } catch (error) {
            console.error(error);
            addNotification('Failed to import any properties', 'error');
        }
    };

    const handleRemoveFile = () => {
        setParsedFile(undefined);
        setParsedFileName(undefined);
        setNewProperties([]);
        setUnknownFields([]);
        setLabelsInCsv([]);
        setHasLabelsColumn(false);
        setLabelsConfirmed(false);
        setPropertyErrors([]);
        setFailedPropertyNames([]);
        setShowAllErrors(false);
        setNumImportedProperties(0);
    };

    const handleRemoveLabel = (labelToRemove: string) => {
        setLabelsInCsv(prev =>
            prev.map(rowLabels => rowLabels.filter(label => label !== labelToRemove))
        );
        setRemovedLabels(prev => [...prev, labelToRemove]);
    };
    const handleAddLabel = (labelToAdd: string) => {
        setLabelsInCsv(prev =>
            prev.map(rowLabels => {
                if (!rowLabels.includes(labelToAdd)) {
                    return [...rowLabels, labelToAdd];
                }
                return rowLabels;
            })
        );
        setRemovedLabels(prev => prev.filter(label => label !== labelToAdd));
    };
    const { newLabels, existingLabelNames } = useMemo(() => {
        if (!existingLabels) {
            return { newLabels: uniqueLabelsInCsv, existingLabelNames: [] };
        }
        const existingSet = new Set(existingLabels.map(l => l.name));
        return {
            newLabels: uniqueLabelsInCsv.filter(label => !existingSet.has(label)),
            existingLabelNames: uniqueLabelsInCsv.filter(label => existingSet.has(label)),
        };
    }, [uniqueLabelsInCsv, existingLabels]);

    const previewTableData = useMemo(() => {
        if (!newProperties.length || !parsedFile) {
            return;
        }

        const fieldsForPreview = hasLabelsColumn ?
            importPropertiesFields :
            importPropertiesFields.filter(f => f !== 'labels');

        const rows = parsedFile.body.map((row, index) => {
            const errors = newProperties[index].errors;

            const rowCells = fieldsForPreview.map((col) => {
                const hasError = errors?.includes(col) ?? false;

                if (col === 'labels') {
                    const rowLabels = labelsInCsv[index] || [];
                    const sortedRowLabels = [...rowLabels].sort((a, b) => a.localeCompare(b));
                    const firstLabel = sortedRowLabels[0];
                    const remainingCount = sortedRowLabels.length - 1;
                    const labelChips =
                        sortedRowLabels.length === 0 ? (
                            ''
                        ) : (
                            <Tooltip title={sortedRowLabels.join(', ')} arrow>
                                <Flex flexWrap="nowrap" gap={0.5} alignItems="center" justifyContent="flex-end">
                                    <LabelChip
                                        key={firstLabel}
                                        name={firstLabel}
                                        color={PROPERTY_LABEL_CHIP_COLOR}
                                        size="body"
                                    />
                                    {remainingCount > 0 && (
                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                                            +{remainingCount}
                                        </Typography>
                                    )}
                                </Flex>
                            </Tooltip>
                        );

                    return {
                        label: labelChips,
                        hasError,
                    };
                }

                return {
                    label: row[col as keyof PropertyCsvRow] as string,
                    hasError,
                };
            });

            return rowCells;
        });

        const columnNames = fieldsForPreview.map(column => {
            return formatString(column, StringFormat.SnakeCase, StringFormat.SentenceCase);
        });

        const errors = newProperties
            .filter((o) => !!o.errors?.length)
            .map((o) => o.errors?.map(error => formatString(error, StringFormat.SnakeCase, StringFormat.SentenceCase)))
            .map((o, i) => [i, o] as [number, string[]]);

        return {
            columns: columnNames,
            rows,
            errors,
        };
    }, [parsedFile, labelsInCsv]);

    const numValidProperties = newProperties.filter(o => !o.errors?.length).length;

    return (
        <Dialog open={isOpened} fullScreen onClose={onClose}>
            <DialogTitle textAlign="center">
                Import Properties
            </DialogTitle>

            <DialogContent>
                <FlexColumn rowGap={2}>
                    {!!info && <Alert severity="info">{info}</Alert>}
                    <FlexColumn>
                        <Typography>
                            Recommended for 5+ units or properties.
                        </Typography>
                        <Typography>
                            Download the provided CSV template, fill it out with the necessary property details, and then upload the completed CSV file back to Reffie to get started.
                        </Typography>
                    </FlexColumn>
                    <Stepper orientation="vertical">
                        <Step active>
                            <StepLabel>
                                Download the CSV template
                            </StepLabel>
                            <StepContent>
                                <ButtonLink
                                    href={propertyImportTemplateUrl}
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    download
                                    data-testid="property-csv-template-download-button"
                                >
                                    Download
                                </ButtonLink>
                            </StepContent>
                        </Step>

                        <Step active>
                            <StepLabel>
                                Upload the filled-in CSV template
                            </StepLabel>
                            <StepContent>
                                <FlexColumn rowGap={1}>
                                    <Flex>
                                        <FileUpload
                                            key={parsedFileCreatedAt}
                                            onChange={handleUploadFile}
                                            data-testid="property-csv-upload-button"
                                        >
                                            {parsedFile ? 'Change file' : 'Upload'}
                                        </FileUpload>
                                        {parsedFile && (
                                            <Button
                                                variant="outlined"
                                                onClick={handleRemoveFile}
                                                data-testid="property-csv-remove-file-button"
                                            >
                                                Remove file
                                            </Button>
                                        )}
                                    </Flex>
                                    {parsedFileName && (
                                        <Typography variant="body2" color="text.secondary">
                                            Uploaded: {parsedFileName}
                                        </Typography>
                                    )}
                                </FlexColumn>
                            </StepContent>
                        </Step>

                        {hasLabelsColumn && newLabels.length > 0 && !labelsConfirmed && (
                            <Step active>
                                <StepLabel>
                                    Review and create labels
                                </StepLabel>
                                <StepContent>
                                    <FlexColumn rowGap={2}>
                                        {!isCreatingLabels && (
                                            <ImportPropertiesLabel
                                                newLabels={newLabels}
                                                existingLabels={existingLabelNames}
                                                onRemoveLabel={handleRemoveLabel}
                                                removedLabels={removedLabels}
                                                onAddLabel={handleAddLabel}
                                            />
                                        )}
                                        <Flex gap={1}>
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setHasLabelsColumn(false);
                                                    setLabelsInCsv([]);
                                                }}
                                                disabled={isCreatingLabels}
                                            >
                                                Skip Labels
                                            </Button>
                                            <LoadingButton
                                                variant="contained"
                                                onClick={handleCreateLabels}
                                                loading={isCreatingLabels}
                                                disabled={!existingLabels}
                                                data-testid="create-labels-button"
                                            >
                                                Create {newLabels.length > 0 ?
                                                    `${newLabels.length} ${pluralize('label', newLabels.length)}` :
                                                    'Labels'}
                                            </LoadingButton>
                                        </Flex>
                                    </FlexColumn>
                                </StepContent>
                            </Step>
                        )}
                        {newLabels.length === 0 && (
                            <Step active>
                                <StepLabel>
                                    Preview the properties to be imported
                                </StepLabel>
                                <StepContent>
                                    {unknownFields.length > 0 && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            The following fields are not recognized: {unknownFields.join(', ')}. Please download the provided CSV template to best avoid any errors.
                                        </Alert>
                                    )}

                                    {propertyErrors.length > 0 && (
                                        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} >
                                            <AlertTitle>The following properties could not be imported:</AlertTitle>
                                            {propertyErrors
                                                .slice(0, showAllErrors ? propertyErrors.length : 2)
                                                .join('\n')}
                                            {propertyErrors.length > 2 && (
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    onClick={() => setShowAllErrors((prev) => !prev)}
                                                >
                                                    {showAllErrors ? 'See less' : 'See more'}
                                                </Button>
                                            )}
                                        </Alert>
                                    )}

                                    {previewTableData && (
                                        <SimpleTable
                                            key={parsedFileCreatedAt}
                                            {...previewTableData}
                                        />
                                    )}

                                    {failedPropertyNames.length > 0 && (
                                        <FlexColumn mt={2} rowGap={4}>
                                            <Alert severity="success">
                                                {numImportedProperties} {pluralize('property', numImportedProperties)} imported successfully.
                                            </Alert>

                                            <FlexColumn>
                                                <Typography>
                                                    Failed to import the following properties
                                                </Typography>
                                                <SimpleTable
                                                    columns={['Property Name']}
                                                    rows={failedPropertyNames.map(name => [{ label: name, hasError: false }])}
                                                />
                                            </FlexColumn>
                                        </FlexColumn>
                                    )}

                                </StepContent>
                            </Step>
                        )}
                    </Stepper>
                </FlexColumn>
            </DialogContent>

            <DialogActions>
                {previewTableData && newLabels.length === 0 && (
                    <Typography mr={2}>
                        {numValidProperties}/{newProperties.length} properties can be imported.
                    </Typography>
                )}
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isCreatingProperties || isCreatingLabels}
                    data-testid="property-import-cancel-button"
                >
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!numValidProperties}
                    loading={isCreatingProperties}
                    data-testid="property-import-submit-button"
                >
                    Import {numValidProperties ?
                        `${numValidProperties} ${pluralize('property', numValidProperties)}` : ''}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
